// Address autocomplete and geocoding with Photon (OpenStreetMap data), ADR 0004 D2.
// Public instance, no key: https://photon.komoot.io — data © OpenStreetMap contributors (ODbL).

export const PHOTON_URL = "https://photon.komoot.io/api/"
export const PHOTON_RESULTS_LIMIT = 5
export const ADDRESS_SEARCH_MIN_CHARS = 3
export const OSM_COPYRIGHT_URL = "https://www.openstreetmap.org/copyright"

type PhotonProperties = {
  type?: string
  name?: string
  housenumber?: string
  street?: string
  postcode?: string
  city?: string
  district?: string
  locality?: string
  county?: string
  state?: string
  country?: string
  countrycode?: string
}

export type PhotonFeature = {
  properties: PhotonProperties
  geometry: { type: "Point"; coordinates: [number, number] } // [longitude, latitude]
}

export type GeocodedAddress = {
  label: string
  address: string
  zipcode: string | null
  city: string | null
  countryCode: string | null
  location: { lat: number; lng: number }
}

const join = (parts: (string | null | undefined)[], separator: string) =>
  parts.filter(part => part !== undefined && part !== null && part.trim() !== "").join(separator)

// Results that are an area (a town, a district…), not a street or a building: Photon puts their
// name in `name`, and they have no address line.
const AREA_TYPES = new Set(["city", "district", "locality", "county", "state", "country"])

export const toGeocodedAddress = (feature: PhotonFeature): GeocodedAddress => {
  const p = feature.properties
  const [lng, lat] = feature.geometry.coordinates
  const isArea = p.type !== undefined && AREA_TYPES.has(p.type)
  const streetLine = join([p.housenumber, p.street], " ")
  // A street or a named place without a street keeps its name as the address line.
  const address = isArea ? "" : streetLine !== "" ? streetLine : (p.name ?? "")
  const city =
    isArea && p.type === "city" ? (p.name ?? null) : (p.city ?? p.locality ?? p.district ?? null)
  const placeName = !isArea && p.name !== address ? p.name : undefined

  return {
    label: join(
      [
        placeName,
        address,
        join([p.postcode, city ?? (isArea ? p.name : undefined)], " "),
        p.country,
      ],
      ", "
    ),
    address,
    zipcode: p.postcode ?? null,
    city,
    countryCode: p.countrycode?.toUpperCase() ?? null,
    location: { lat, lng },
  }
}

// Photon often returns the same place several times (one per OpenStreetMap object).
export const uniqueByLabel = (addresses: GeocodedAddress[]) =>
  addresses.filter(
    (address, index) => addresses.findIndex(other => other.label === address.label) === index
  )

// Photon supports a few languages for place names; others fall back to local names.
const photonLanguage = (locale: string) => (locale.toLowerCase().startsWith("fr") ? "fr" : "en")

export const searchAddresses = async (
  query: string,
  locale: string,
  signal?: AbortSignal
): Promise<GeocodedAddress[]> => {
  const params = new URLSearchParams({
    q: query,
    limit: String(PHOTON_RESULTS_LIMIT),
    lang: photonLanguage(locale),
  })
  const response = await fetch(`${PHOTON_URL}?${params.toString()}`, { signal })
  if (!response.ok) throw new Error(`Photon responded ${response.status}`)
  const body = (await response.json()) as { features?: PhotonFeature[] }

  return uniqueByLabel((body.features ?? []).map(toGeocodedAddress))
}
